'use client';

import { useTranslations } from 'next-intl';
import { useMounted } from '@/hooks/useMounted';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import type { PlayerStanding } from '@/draw/types';
import { standingShortName } from '../shortName';
import { SectionLabel } from './SectionLabel';
import { ChartTooltip } from './ChartTooltip';

interface GamesChartProps {
  standings: PlayerStanding[];
  isDark?: boolean;
}

export function GamesChart({ standings, isDark = true }: GamesChartProps) {
  const t = useTranslations('Celebration');
  const mounted = useMounted();

  const data = standings.map((p) => ({
    name: standingShortName(p),
    [t('wins')]: p.gamesWon,
    lost: p.gamesLost,
  }));

  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : '#E8E2D4';
  const tickColor = isDark ? 'rgba(255,255,255,0.4)' : '#1B2D4F80';
  const axisColor = isDark ? 'rgba(255,255,255,0.08)' : '#E8E2D4';
  const wonColor = isDark ? '#FBBF24' : '#F5A623';
  const lostColor = isDark ? 'rgba(147,161,173,0.35)' : '#1B2D4F20';

  return (
    <>
      <SectionLabel>{t('gamesWonLost')}</SectionLabel>
      {mounted && (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} layout="vertical" barSize={18}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fill: tickColor, fontSize: 11 }}
              axisLine={{ stroke: axisColor }}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: tickColor, fontSize: 12 }}
              axisLine={{ stroke: axisColor }}
              width={60}
            />
            <Tooltip content={<ChartTooltip isDark={isDark} />} />
            <Bar dataKey={t('wins')} stackId="a" fill={wonColor} />
            <Bar dataKey="lost" name="Lost" stackId="a" fill={lostColor} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </>
  );
}
